/**
 * Content Configuration API
 *
 * API client for managing content configuration including brand guidelines,
 * channel strategies, and tactics.
 */
import apiClient from './apiClient';

// ========== Types ==========

export type TacticCategory = 'Engagement' | 'Story/Content Amplification' | 'Down-Funnel Action' | 'Brand Awareness' | 'Audience Growth';
export type TacticPriority = 'low' | 'medium' | 'high';
export type TacticReadiness = 'alpha' | 'beta' | 'gamma';
export type TacticPostType = 'single' | 'carousel' | 'video';
export type TacticImageMode = 'article_media' | 'generated' | 'none';

export interface TacticFrontmatter {
  id: string;
  name: string;
  platforms: string[];
  category: TacticCategory;
  priority: TacticPriority;
  post_type: TacticPostType;
  image: TacticImageMode;
  caption: boolean;
  link: boolean;
  hashtags: boolean;
  first_comment: boolean;
  thread: boolean;
  // Take (phase 1): every tend from this tactic asks for a to-camera
  // recording and lands in the brand's Take queue. Independent of post_type
  // (carousel takes allowed). Absent = false.
  take?: boolean;
  // Carousel slide count bounds (editorial). null/undefined = unset —
  // the carousel prompt's default range governs.
  slides_min?: number | null;
  slides_max?: number | null;
  // Channel surface (PLACEMENTS_BY_CHANNEL — snapchat: story/saved_story/
  // spotlight). null/undefined = channel default.
  placement?: string | null;
  readiness: TacticReadiness;
}

export interface TacticSummary {
  id: string;
  name: string;
  category: string;
  priority: string;
  readiness: string;
  is_enabled: boolean;
  has_brand_override: boolean;
  /** Frontmatter `take: true` — every tend from this tactic is a take request. */
  take?: boolean;
}

export interface ChannelOverview {
  platform: string;
  tactics_count: number;
  enabled_tactics_count: number;
  has_strategy_override: boolean;
}

export interface ConfigOverviewResponse {
  brand_slug: string;
  has_brand_guidelines_override: boolean;
  channels: ChannelOverview[];
}

export interface BrandGuidelinesResponse {
  content: string;
  is_brand_override: boolean;
  last_updated: string | null;
}

export interface ChannelConfigResponse {
  platform: string;
  strategy_content: string;
  has_strategy_override: boolean;
  tactics: TacticSummary[];
  disabled_tactics: string[];
}

export interface TacticDetailResponse {
  id: string;
  name: string;
  platforms: string[];
  category: string;
  priority: string;
  post_type: string;
  image: string;
  caption: boolean;
  link: boolean;
  hashtags: boolean;
  first_comment: boolean;
  thread: boolean;
  take?: boolean;
  slides_min?: number | null;
  slides_max?: number | null;
  placement?: string | null;
  readiness: string;
  content: string;
  has_brand_override: boolean;
  is_enabled: boolean;
}

export interface TacticUpdateRequest {
  frontmatter: TacticFrontmatter;
  content: string;
}

export interface SuccessResponse {
  success: boolean;
  message: string;
}

// ========== API Functions ==========

// ========== Playbooks (agent phase 2) ==========

export type PlaybookLayer = 'builtin' | 'universal' | 'brand';

export interface PlaybookSummary {
  name: string;
  description: string;
  layer: PlaybookLayer;
  version: string;
  based_on_version?: string | null;
  beneath_version?: string | null;
  stale: boolean;
  kind?: string | null;
  output?: string | null;
  channels: string[];
  post_type?: string | null;
  readiness?: string | null;
  requires: string[];
  files: string[];
  has_brand_override: boolean;
  has_universal_override: boolean;
  has_builtin: boolean;
}

export interface PlaybookDetail extends PlaybookSummary {
  body: string;
  metadata: Record<string, string>;
}

export interface PlaybookSaveRequest {
  description: string;
  body: string;
  metadata: Record<string, string>;
}

export const configApi = {
  /**
   * Get configuration overview for a brand
   */
  async getOverview(brandSlug: string): Promise<ConfigOverviewResponse> {
    return apiClient.get<ConfigOverviewResponse>(`config/overview/${brandSlug}`);
  },

  /**
   * Get brand guidelines (brand-specific or universal fallback)
   */
  async getBrandGuidelines(brandSlug: string): Promise<BrandGuidelinesResponse> {
    return apiClient.get<BrandGuidelinesResponse>(`config/brand-guidelines/${brandSlug}`);
  },

  /**
   * Update brand guidelines (creates brand override)
   */
  async updateBrandGuidelines(brandSlug: string, content: string): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>(`config/brand-guidelines/${brandSlug}`, { content });
  },

  /**
   * Get channel configuration including strategy and tactics list
   */
  async getChannelConfig(brandSlug: string, platform: string): Promise<ChannelConfigResponse> {
    return apiClient.get<ChannelConfigResponse>(`config/channels/${brandSlug}/${platform}`);
  },

  /**
   * Update channel strategy (creates brand override)
   */
  async updateChannelStrategy(brandSlug: string, platform: string, content: string): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>(`config/channels/${brandSlug}/${platform}/strategy`, { content });
  },

  /**
   * Drop the brand's own strategy for a channel so it reads the universal
   * one again. Answers the universal text so the editor can show it at once.
   */
  async revertChannelStrategy(
    brandSlug: string,
    platform: string
  ): Promise<SuccessResponse & { strategy_content: string }> {
    return apiClient.delete<SuccessResponse & { strategy_content: string }>(
      `config/channels/${brandSlug}/${platform}/strategy`
    );
  },

  /**
   * Get full tactic details
   */
  async getTacticDetail(brandSlug: string, platform: string, tacticId: string): Promise<TacticDetailResponse> {
    return apiClient.get<TacticDetailResponse>(`config/channels/${brandSlug}/${platform}/tactics/${tacticId}`);
  },

  /**
   * Update a tactic (creates brand override)
   */
  async updateTactic(
    brandSlug: string,
    platform: string,
    tacticId: string,
    request: TacticUpdateRequest
  ): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>(
      `config/channels/${brandSlug}/${platform}/tactics/${tacticId}`,
      request
    );
  },

  /**
   * Toggle tactic enabled/disabled state
   */
  async toggleTactic(
    brandSlug: string,
    platform: string,
    tacticId: string,
    enabled: boolean
  ): Promise<SuccessResponse & { is_enabled: boolean }> {
    return apiClient.patch<SuccessResponse & { is_enabled: boolean }>(
      `config/channels/${brandSlug}/${platform}/tactics/${tacticId}/toggle`,
      { enabled }
    );
  },

  // ========== Playbooks ==========

  async listPlaybooks(brandSlug: string): Promise<PlaybookSummary[]> {
    return apiClient.get<PlaybookSummary[]>(`config/playbooks/${brandSlug}`);
  },

  async getPlaybook(brandSlug: string, name: string): Promise<PlaybookDetail> {
    return apiClient.get<PlaybookDetail>(`config/playbooks/${brandSlug}/${name}`);
  },

  /** Create or update the brand's copy (it replaces the universal / built-in for this brand). */
  async savePlaybook(brandSlug: string, name: string, request: PlaybookSaveRequest): Promise<SuccessResponse & { playbook: PlaybookSummary }> {
    return apiClient.put<SuccessResponse & { playbook: PlaybookSummary }>(`config/playbooks/${brandSlug}/${name}`, request);
  },

  async createPlaybook(brandSlug: string, request: PlaybookSaveRequest & { name: string }): Promise<SuccessResponse & { playbook: PlaybookSummary }> {
    return apiClient.post<SuccessResponse & { playbook: PlaybookSummary }>(`config/playbooks/${brandSlug}`, request);
  },

  /** Remove the brand's copy; the layer beneath applies again. */
  async deletePlaybook(brandSlug: string, name: string): Promise<SuccessResponse & { has_fallback: boolean }> {
    return apiClient.delete<SuccessResponse & { has_fallback: boolean }>(`config/playbooks/${brandSlug}/${name}`);
  },

  async listUniversalPlaybooks(): Promise<PlaybookSummary[]> {
    return apiClient.get<PlaybookSummary[]>('config/universal/playbooks');
  },

  async getUniversalPlaybook(name: string): Promise<PlaybookDetail> {
    return apiClient.get<PlaybookDetail>(`config/universal/playbooks/${name}`);
  },

  async saveUniversalPlaybook(name: string, request: PlaybookSaveRequest): Promise<SuccessResponse & { playbook: PlaybookSummary }> {
    return apiClient.put<SuccessResponse & { playbook: PlaybookSummary }>(`config/universal/playbooks/${name}`, request);
  },

  async deleteUniversalPlaybook(name: string): Promise<SuccessResponse & { has_fallback: boolean }> {
    return apiClient.delete<SuccessResponse & { has_fallback: boolean }>(`config/universal/playbooks/${name}`);
  },

  // ========== SuperAdmin Universal Endpoints ==========

  /**
   * Get universal brand guidelines (SuperAdmin only)
   */
  async getUniversalBrandGuidelines(): Promise<BrandGuidelinesResponse> {
    return apiClient.get<BrandGuidelinesResponse>('config/universal/brand-guidelines');
  },

  /**
   * Update universal brand guidelines (SuperAdmin only)
   */
  async updateUniversalBrandGuidelines(content: string): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>('config/universal/brand-guidelines', { content });
  },

  /**
   * Get universal channel configuration (SuperAdmin only)
   */
  async getUniversalChannelConfig(platform: string): Promise<ChannelConfigResponse> {
    return apiClient.get<ChannelConfigResponse>(`config/universal/channels/${platform}`);
  },

  /**
   * Update universal channel strategy (SuperAdmin only)
   */
  async updateUniversalChannelStrategy(platform: string, content: string): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>(`config/universal/channels/${platform}/strategy`, { content });
  },

  /**
   * Get universal tactic details (SuperAdmin only)
   */
  async getUniversalTacticDetail(platform: string, tacticId: string): Promise<TacticDetailResponse> {
    return apiClient.get<TacticDetailResponse>(`config/universal/channels/${platform}/tactics/${tacticId}`);
  },

  /**
   * Update universal tactic (SuperAdmin only)
   */
  async updateUniversalTactic(
    platform: string,
    tacticId: string,
    request: TacticUpdateRequest
  ): Promise<SuccessResponse> {
    return apiClient.put<SuccessResponse>(
      `config/universal/channels/${platform}/tactics/${tacticId}`,
      request
    );
  },

  /**
   * Create a new tactic for a brand
   */
  async createTactic(
    brandSlug: string,
    platform: string,
    name: string
  ): Promise<SuccessResponse & { tactic_id: string }> {
    return apiClient.post<SuccessResponse & { tactic_id: string }>(
      `config/channels/${brandSlug}/${platform}/tactics`,
      { name }
    );
  },

  /**
   * Create a new universal tactic (SuperAdmin only)
   */
  async createUniversalTactic(
    platform: string,
    name: string
  ): Promise<SuccessResponse & { tactic_id: string }> {
    return apiClient.post<SuccessResponse & { tactic_id: string }>(
      `config/universal/channels/${platform}/tactics`,
      { name }
    );
  },

  /**
   * Delete a brand tactic override
   */
  async deleteTactic(
    brandSlug: string,
    platform: string,
    tacticId: string
  ): Promise<SuccessResponse & { has_universal_fallback: boolean }> {
    return apiClient.delete<SuccessResponse & { has_universal_fallback: boolean }>(
      `config/channels/${brandSlug}/${platform}/tactics/${tacticId}`
    );
  },

  /**
   * Delete a universal tactic (SuperAdmin only)
   */
  async deleteUniversalTactic(
    platform: string,
    tacticId: string
  ): Promise<SuccessResponse & { has_universal_fallback: boolean }> {
    return apiClient.delete<SuccessResponse & { has_universal_fallback: boolean }>(
      `config/universal/channels/${platform}/tactics/${tacticId}`
    );
  }
};

// Named exports
export const {
  getOverview,
  getBrandGuidelines,
  updateBrandGuidelines,
  getChannelConfig,
  updateChannelStrategy,
  revertChannelStrategy,
  getTacticDetail,
  updateTactic,
  toggleTactic,
  createTactic,
  deleteTactic,
  getUniversalBrandGuidelines,
  updateUniversalBrandGuidelines,
  getUniversalChannelConfig,
  updateUniversalChannelStrategy,
  getUniversalTacticDetail,
  updateUniversalTactic,
  createUniversalTactic,
  deleteUniversalTactic
} = configApi;

export default configApi;
